import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardHeader,
  CardBody,
  Text,
  Button,
  VStack,
  HStack,
  Grid,
  GridItem,
  Badge,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  Switch,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  useToast,
  Divider,
  IconButton,
  Tooltip,
  Select,
  Flex,
  Spacer,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Code,
  Progress,
  Menu,
  MenuButton,
  MenuList,
  MenuItem
} from '@chakra-ui/react';

import { useGlobalAction, useUser, useFindMany } from '@gadgetinc/react';
import { api } from '../api';
import { hasRole } from "../lib/roleUtils";

const AgentControlPanel = () => {
  const user = useUser();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState(0);
  const [editingAgent, setEditingAgent] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [chatHistory, setChatHistory] = useState([]);
  const [debugInfo, setDebugInfo] = useState({});
  const [showDebug, setShowDebug] = useState(false);
  
  // API hooks
  const [{ data: configurations, fetching: fetchingConfigs, error: configsError }, refetchConfigs] = useFindMany(api.agentConfiguration);
  const [{ data: vectorStores, fetching: fetchingVectorStores, error: vectorStoresError }] = useFindMany(api.vectorStore);
  const [{ data: setupResult, fetching: settingUp, error: setupError }, runSetup] = useGlobalAction(api.setupAgentConfigurations);
  const [{ data: testResult, fetching: testing, error: testError }, runTest] = useGlobalAction(api.testAgentsSDK);
  const [{ data: updateResult, fetching: updating, error: updateError }, runUpdate] = useGlobalAction(api.updateAgentConfigurationGlobal);
  const [{ data: syncResult, fetching: syncing, error: syncError }, runSync] = useGlobalAction(api.syncAgentsToVM);

  const { isOpen: isTestModalOpen, onOpen, onClose: closeTestModal } = useDisclosure();
  const { isOpen: isChatModalOpen, onOpen: openChatModal, onClose: closeChatModal } = useDisclosure();
  const [testingAgent, setTestingAgent] = useState(null);
  const [testInput, setTestInput] = useState('');
  const [currentMessage, setCurrentMessage] = useState('');

  // Check permissions
  if (!user || !(hasRole(user, "system-admin") || hasRole(user, "admin"))) {
    return (
      <Container maxW="container.lg" py={8}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Access Denied!</AlertTitle>
          <AlertDescription>
            You must be a system administrator or admin to access the Agent Control Panel.
          </AlertDescription>
        </Alert>
      </Container>
    );
  }

  // Function to get display information for agents from database configurations
  const getAgentDisplayInfo = (config) => {
    const agentTypeMap = {
      'don': {
        icon: '👔',
        color: 'blue',
        fullName: 'Documentation and Operations Navigator'
      },
      'maia': {
        icon: '🩺',
        color: 'green',
        fullName: 'Medical Intelligence Assistant'
      },
      'gerald': {
        icon: '📚',
        color: 'purple',
        fullName: 'General Education and Resource Assistant'
      }
    };

    const defaultInfo = {
      icon: '🤖',
      color: 'gray',
      fullName: config.name || 'AI Agent'
    };

    const typeInfo = agentTypeMap[config.agentType] || defaultInfo;
    
    // Extract capabilities from database - get the keys where the value is true
    let capabilities = [];
    if (config.capabilities && typeof config.capabilities === 'object') {
      capabilities = Object.keys(config.capabilities).filter(key => config.capabilities[key] === true);
    } else if (config.enabledTools && Array.isArray(config.enabledTools)) {
      capabilities = config.enabledTools;
    }

    return {
      id: config.agentType,
      name: config.name || config.agentType?.toUpperCase(),
      fullName: config.name || typeInfo.fullName,
      description: config.description || 'AI Agent configuration',
      color: typeInfo.color,
      icon: typeInfo.icon,
      capabilities: capabilities
    };
  };

  const getAgentConfig = (agentType) => {
    return configurations?.find(config => config.agentType === agentType);
  };

  const handleSetupAgents = async () => {
    try {
      const result = await runSetup();
      setDebugInfo(prev => ({
        ...prev,
        lastSetup: {
          timestamp: new Date().toISOString(),
          result: result,
          success: true
        }
      }));
      toast({
        title: 'Setup Complete',
        description: 'Agent configurations have been initialized successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      refetchConfigs();
    } catch (error) {
      setDebugInfo(prev => ({
        ...prev,
        lastSetup: {
          timestamp: new Date().toISOString(),
          error: error.message,
          success: false
        }
      }));
      toast({
        title: 'Setup Failed',
        description: error.message || 'Failed to setup agent configurations.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleTestAgent = async (agentType, input) => {
    try {
      const startTime = Date.now();
      const result = await runTest({ agentType, input });
      const endTime = Date.now();
      
      const testData = {
        timestamp: new Date().toISOString(),
        input,
        result,
        responseTime: endTime - startTime,
        success: true
      };

      setTestResults(prev => ({
        ...prev,
        [agentType]: testData
      }));

      setDebugInfo(prev => ({
        ...prev,
        [agentType]: testData
      }));

      toast({
        title: 'Test Complete',
        description: `${agentType.toUpperCase()} agent tested successfully in ${testData.responseTime}ms.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      const testData = {
        timestamp: new Date().toISOString(),
        input,
        error: error.message,
        success: false
      };

      setTestResults(prev => ({
        ...prev,
        [agentType]: testData
      }));

      setDebugInfo(prev => ({
        ...prev,
        [agentType]: testData
      }));

      toast({
        title: 'Test Failed',
        description: error.message || `Failed to test ${agentType} agent.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleUpdateConfig = async (agentId, updates) => {
    console.log('=== UPDATE CONFIG DEBUG ===');
    console.log('handleUpdateConfig called with agentId:', agentId);
    console.log('handleUpdateConfig called with updates:', updates);
    console.log('updates.enabledTools type:', typeof updates.enabledTools);
    console.log('updates.enabledTools value:', updates.enabledTools);
    console.log('updates.enabledTools Array.isArray:', Array.isArray(updates.enabledTools));
    
    // Ensure enabledTools is always an array
    const safeUpdates = {
      ...updates,
      enabledTools: Array.isArray(updates.enabledTools) ? updates.enabledTools : []
    };
    
    console.log('safeUpdates after processing:', safeUpdates);
    console.log('safeUpdates.enabledTools after processing:', safeUpdates.enabledTools);
    
    try {
      console.log('About to call updateAgentConfigurationGlobal with payload:', { id: agentId, ...safeUpdates });
      const result = await runUpdate({ id: agentId, ...safeUpdates });
      console.log('Update result:', result);
      
      setDebugInfo(prev => ({
        ...prev,
        lastUpdate: {
          timestamp: new Date().toISOString(),
          agentId,
          updates: safeUpdates,
          result,
          success: true
        }
      }));
      
      toast({
        title: 'Configuration Updated',
        description: 'Agent configuration has been saved successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      setEditingAgent(null);
      
      // Force a refetch of configurations
      console.log('Calling refetchConfigs...');
      await refetchConfigs();
      console.log('Refetch completed');
      
    } catch (error) {
      console.error('=== UPDATE FAILED ===');
      console.error('Update failed with error:', error);
      console.error('Error message:', error.message);
      console.error('Error details:', error);
      
      setDebugInfo(prev => ({
        ...prev,
        lastUpdate: {
          timestamp: new Date().toISOString(),
          agentId,
          updates: safeUpdates,
          error: error.message,
          success: false
        }
      }));
      
      toast({
        title: 'Update Failed',
        description: error.message || 'Failed to update agent configuration.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const openTestModal = (config) => {
    const agentInfo = getAgentDisplayInfo(config);
    setTestingAgent({ ...agentInfo, config });
    setTestInput('');
    onOpen();
  };

  const openChatInterface = (config) => {
    const agentInfo = getAgentDisplayInfo(config);
    setTestingAgent({ ...agentInfo, config });
    setChatHistory([]);
    setCurrentMessage('');
    openChatModal();
  };

  const sendChatMessage = async () => {
    if (!currentMessage.trim() || !testingAgent) return;

    const userMessage = {
      type: 'user',
      content: currentMessage,
      timestamp: new Date().toISOString()
    };

    setChatHistory(prev => [...prev, userMessage]);
    setCurrentMessage('');

    try {
      const startTime = Date.now();
      const result = await runTest({ agentType: testingAgent.id, input: currentMessage });
      const endTime = Date.now();

      const agentMessage = {
        type: 'agent',
        content: result?.response || result?.data?.response || 'No response received from agent',
        timestamp: new Date().toISOString(),
        responseTime: endTime - startTime,
        rawResult: result // for debugging
      };

      setChatHistory(prev => [...prev, agentMessage]);

      setDebugInfo(prev => ({
        ...prev,
        [`${testingAgent.id}_chat`]: {
          lastMessage: userMessage.content,
          lastResponse: agentMessage.content,
          responseTime: agentMessage.responseTime,
          timestamp: agentMessage.timestamp
        }
      }));

    } catch (error) {
      const errorMessage = {
        type: 'error',
        content: error.message || 'Failed to get response',
        timestamp: new Date().toISOString()
      };

      setChatHistory(prev => [...prev, errorMessage]);
    }
  };

  const AgentCard = ({ config }) => {
    const agent = getAgentDisplayInfo(config);
    const lastModified = config?.updatedAt ? new Date(config.updatedAt).toLocaleDateString() : 'Never';
    
    return (
      <Card style={{
        background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
        border: '1px solid #4a5568',
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <CardHeader style={{ background: 'rgba(0, 0, 0, 0.2)', borderBottom: '1px solid #4a5568' }}>
          <HStack justify="space-between">
            <VStack align="start" spacing={1}>
              <HStack>
                <Text fontSize="2xl">{agent.icon}</Text>
                <Heading size="md" color="white">{agent.name}</Heading>
                <Badge colorScheme={config?.isActive ? 'green' : 'red'}>
                  {config?.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </HStack>
              <Text fontSize="sm" color="gray.300">{agent.fullName}</Text>
            </VStack>
            <Menu>
              <MenuButton as={IconButton} icon={'⚙️'} variant="ghost" color="white" />
              <MenuList bg="gray.700" border="1px solid #4a5568">
                <MenuItem bg="gray.700" color="white" onClick={() => openTestModal(config)}>
                  Test Agent
                </MenuItem>
                <MenuItem bg="gray.700" color="white" onClick={() => openChatInterface(config)}>
                  Chat Interface
                </MenuItem>
                <MenuItem bg="gray.700" color="white" onClick={() => setEditingAgent(config)}>
                  Edit Configuration
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <Text color="gray.300" fontSize="sm">
              {agent.description}
            </Text>
            
            <Box>
              <Text fontSize="sm" fontWeight="bold" color="white" mb={2}>Capabilities:</Text>
              <Grid templateColumns="repeat(auto-fit, minmax(150px, 1fr))" gap={1}>
                {agent.capabilities.map((capability, index) => (
                  <Badge key={index} size="sm" colorScheme={agent.color} variant="subtle">
                    {capability}
                  </Badge>
                ))}
              </Grid>
            </Box>

            <Divider borderColor="gray.600" />

            <VStack spacing={2} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="xs" color="gray.400">Status:</Text>
                <Badge colorScheme={config?.isActive ? 'green' : 'red'} size="sm">
                  {config?.isActive ? 'Running' : 'Stopped'}
                </Badge>
              </HStack>
              
              <HStack justify="space-between">
                <Text fontSize="xs" color="gray.400">Last Modified:</Text>
                <Text fontSize="xs" color="gray.300">{lastModified}</Text>
              </HStack>
              
              {testResults[agent.id] && (
                <HStack justify="space-between">
                  <Text fontSize="xs" color="gray.400">Last Test:</Text>
                  <Badge colorScheme={testResults[agent.id].success ? 'green' : 'red'} size="sm">
                    {testResults[agent.id].success ? 'Pass' : 'Fail'}
                  </Badge>
                </HStack>
              )}
            </VStack>

            <HStack spacing={2}>
              <Button 
                size="sm" 
                colorScheme={agent.color} 
                variant="solid"
                onClick={() => openTestModal(config)}
                isDisabled={!config?.isActive}
              >
                Quick Test
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                colorScheme="gray"
                onClick={() => openChatInterface(config)}
                isDisabled={!config?.isActive}
              >
                Chat
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    );
  };

  const ConfigurationEditor = ({ config, onSave, onCancel }) => {
    // Valid OpenAI Agent SDK tool types
    const VALID_TOOL_TYPES = ['file_search', 'web_search', 'code_interpreter', 'function_calling'];
    
    const [formData, setFormData] = useState({
      agentType: config?.agentType || '',
      name: config?.name || '',
      description: config?.description || '',
      isActive: config?.isActive !== undefined ? config.isActive : false,
      instructions: config?.instructions?.markdown || '',
      temperature: config?.modelSettings?.temperature || 0.7,
      topP: config?.modelSettings?.topP || 1.0,
      maxTokens: config?.modelSettings?.maxTokens || 1000,
      model: config?.modelSettings?.model || 'gpt-4o',
      modelSettings: config?.modelSettings || {},
      guardrailSettings: config?.guardrailSettings || {},
      enabledTools: Array.isArray(config?.enabledTools) ? config.enabledTools.filter(tool => VALID_TOOL_TYPES.includes(tool)) : [],
      capabilities: config?.capabilities || {},
      functionTools: {
        function_calling: (Array.isArray(config?.enabledTools) && config?.enabledTools.includes('function_calling')) || false,
        file_search: (Array.isArray(config?.enabledTools) && config?.enabledTools.includes('file_search')) || false,
        web_search: (Array.isArray(config?.enabledTools) && config?.enabledTools.includes('web_search')) || false,
        code_interpreter: (Array.isArray(config?.enabledTools) && config?.enabledTools.includes('code_interpreter')) || false,
      },
      vectorStoreId: config?.vectorStoreId || ''
    });

    const handleToolToggle = (toolName, enabled) => {
      // Validate tool name
      if (!VALID_TOOL_TYPES.includes(toolName)) {
        console.warn(`Invalid tool type: ${toolName}. Valid types are:`, VALID_TOOL_TYPES);
        return;
      }

      setFormData(prev => {
        const newFunctionTools = {
          ...prev.functionTools,
          [toolName]: enabled
        };
        
        // Update enabledTools array based on function tools, ensuring only valid tools
        const enabledToolsList = Object.keys(newFunctionTools)
          .filter(key => newFunctionTools[key] && VALID_TOOL_TYPES.includes(key));
        
        return {
          ...prev,
          functionTools: newFunctionTools,
          enabledTools: enabledToolsList
        };
      });
    };

    const handleSyncToVM = async () => {
      try {
        await runSync({ agentId: config?.id });
        toast({
          title: 'Sync Complete',
          description: 'Agent has been synced to VM successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: 'Sync Failed',
          description: error.message || 'Failed to sync agent to VM.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    const handleSave = () => {
      console.log('=== CONFIG EDITOR SAVE DEBUG ===');
      console.log('Original config received:', config);
      console.log('Current formData:', formData);
      console.log('formData.enabledTools:', formData.enabledTools);
      console.log('formData.enabledTools type:', typeof formData.enabledTools);
      console.log('formData.enabledTools Array.isArray:', Array.isArray(formData.enabledTools));
      
      // Validate enabled tools before saving
      const validEnabledTools = Array.isArray(formData.enabledTools) 
        ? formData.enabledTools.filter(tool => VALID_TOOL_TYPES.includes(tool))
        : [];
        
      // Warning if invalid tools were filtered out
      if (formData.enabledTools.length !== validEnabledTools.length) {
        console.warn('Some invalid tools were filtered out:', {
          original: formData.enabledTools,
          filtered: validEnabledTools,
          validTypes: VALID_TOOL_TYPES
        });
      }
      
      // Prepare the data structure for the updateAgentConfigurationGlobal action
      const saveData = {
        agentType: formData.agentType,
        name: formData.name,
        description: formData.description,
        instructions: formData.instructions,
        isActive: formData.isActive,
        modelSettings: {
          ...formData.modelSettings,
          temperature: formData.temperature,
          topP: formData.topP,
          maxTokens: formData.maxTokens,
          model: formData.model
        },
        guardrailSettings: formData.guardrailSettings,
        enabledTools: validEnabledTools, // Only valid tools
        capabilities: formData.capabilities,
        vectorStoreId: formData.vectorStoreId
      };
      
      console.log('saveData prepared:', saveData);
      console.log('saveData.enabledTools (validated):', saveData.enabledTools);
      console.log('saveData.enabledTools type:', typeof saveData.enabledTools);
      console.log('saveData.enabledTools Array.isArray:', Array.isArray(saveData.enabledTools));
      console.log('Valid tool types used:', VALID_TOOL_TYPES);
      console.log('Calling onSave with config.id:', config?.id);
      
      onSave(config?.id, saveData);
    };

    return (
      <VStack spacing={6} align="stretch">
        {/* Basic Configuration Section */}
        <Box>
          <Heading size="md" color="white" mb={4}>Basic Configuration</Heading>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel color="white">Agent Type</FormLabel>
              <Input 
                value={formData.agentType} 
                isReadOnly 
                bg="gray.700" 
                color="white"
                border="1px solid #4a5568"
              />
            </FormControl>

            <FormControl>
              <FormLabel color="white">Name</FormLabel>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                bg="gray.700" 
                color="white"
                border="1px solid #4a5568"
                placeholder="Agent display name"
              />
            </FormControl>

            <FormControl>
              <FormLabel color="white">Description</FormLabel>
              <Input 
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                bg="gray.700" 
                color="white"
                border="1px solid #4a5568"
                placeholder="Brief description of the agent"
              />
            </FormControl>

            <FormControl>
              <FormLabel color="white">Status</FormLabel>
              <HStack>
                <Switch 
                  isChecked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  colorScheme="green"
                />
                <Text color="white">{formData.isActive ? 'Active' : 'Inactive'}</Text>
              </HStack>
            </FormControl>
          </VStack>
        </Box>

        <Divider borderColor="gray.600" />

        {/* Model Settings Section */}
        <Box>
          <Heading size="md" color="white" mb={4}>Model Settings</Heading>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel color="white">Model</FormLabel>
              <Select 
                value={formData.model}
                onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                bg="gray.700" 
                color="white"
                border="1px solid #4a5568"
              >
                <option value="gpt-4o">GPT-4o</option>
                <option value="gpt-4o-mini">GPT-4o Mini</option>
                <option value="o1-preview">O1 Preview</option>
                <option value="o1-mini">O1 Mini</option>
                <option value="gpt-4">GPT-4</option>
                <option value="gpt-4-turbo">GPT-4 Turbo</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel color="white">Temperature: {formData.temperature}</FormLabel>
              <Box px={3}>
                <Input 
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={formData.temperature}
                  onChange={(e) => setFormData(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                />
                <HStack justify="space-between" mt={1}>
                  <Text fontSize="xs" color="gray.400">More Focused</Text>
                  <Text fontSize="xs" color="gray.400">More Creative</Text>
                </HStack>
              </Box>
            </FormControl>

            <FormControl>
              <FormLabel color="white">Top P: {formData.topP}</FormLabel>
              <Box px={3}>
                <Input 
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={formData.topP}
                  onChange={(e) => setFormData(prev => ({ ...prev, topP: parseFloat(e.target.value) }))}
                />
                <HStack justify="space-between" mt={1}>
                  <Text fontSize="xs" color="gray.400">Deterministic</Text>
                  <Text fontSize="xs" color="gray.400">Diverse</Text>
                </HStack>
              </Box>
            </FormControl>

            <FormControl>
              <FormLabel color="white">Max Tokens</FormLabel>
              <Input 
                type="number"
                value={formData.maxTokens}
                onChange={(e) => setFormData(prev => ({ ...prev, maxTokens: parseInt(e.target.value) }))}
                bg="gray.700" 
                color="white"
                border="1px solid #4a5568"
                min="1"
                max="4000"
              />
            </FormControl>
          </VStack>
        </Box>

        <Divider borderColor="gray.600" />

        {/* OpenAI Agent SDK Tools Section */}
        <Box>
          <Heading size="md" color="white" mb={4}>OpenAI Agent SDK Tools</Heading>
          <Text color="gray.400" fontSize="sm" mb={4}>
            Configure which OpenAI tools this agent can use. Only valid SDK tool types are supported.
          </Text>
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <FormControl>
              <HStack>
                <Switch 
                  isChecked={formData.functionTools.file_search}
                  onChange={(e) => handleToolToggle('file_search', e.target.checked)}
                  colorScheme="purple"
                />
                <VStack align="start" spacing={0}>
                  <Text color="white" fontSize="sm">File Search Tool</Text>
                  <Text color="gray.400" fontSize="xs">Search through vector stores and uploaded files</Text>
                </VStack>
              </HStack>
            </FormControl>

            <FormControl>
              <HStack>
                <Switch 
                  isChecked={formData.functionTools.web_search}
                  onChange={(e) => handleToolToggle('web_search', e.target.checked)}
                  colorScheme="green"
                />
                <VStack align="start" spacing={0}>
                  <Text color="white" fontSize="sm">Web Search Tool</Text>
                  <Text color="gray.400" fontSize="xs">Search the internet for current information</Text>
                </VStack>
              </HStack>
            </FormControl>

            <FormControl>
              <HStack>
                <Switch 
                  isChecked={formData.functionTools.code_interpreter}
                  onChange={(e) => handleToolToggle('code_interpreter', e.target.checked)}
                  colorScheme="orange"
                />
                <VStack align="start" spacing={0}>
                  <Text color="white" fontSize="sm">Code Interpreter Tool</Text>
                  <Text color="gray.400" fontSize="xs">Execute Python code and analyze data</Text>
                </VStack>
              </HStack>
            </FormControl>

            <FormControl>
              <HStack>
                <Switch 
                  isChecked={formData.functionTools.function_calling}
                  onChange={(e) => handleToolToggle('function_calling', e.target.checked)}
                  colorScheme="blue"
                />
                <VStack align="start" spacing={0}>
                  <Text color="white" fontSize="sm">Function Calling</Text>
                  <Text color="gray.400" fontSize="xs">Call custom functions and tools</Text>
                </VStack>
              </HStack>
            </FormControl>
          </Grid>

          {/* Vector Store Selection - Only shown when file_search is enabled */}
          {formData.functionTools.file_search && (
            <Box mt={4} p={4} bg="purple.900" borderRadius="md" border="1px solid purple.600">
              <VStack align="stretch" spacing={3}>
                <HStack>
                  <Text color="purple.200" fontSize="sm" fontWeight="bold">📁 File Search Configuration</Text>
                </HStack>
                <Text color="purple.100" fontSize="xs">
                  File Search Tool requires a vector store to search through files. Select one below:
                </Text>
                <FormControl>
                  <FormLabel color="white">Vector Store</FormLabel>
                  <Select 
                    value={formData.vectorStoreId}
                    onChange={(e) => setFormData(prev => ({ ...prev, vectorStoreId: e.target.value }))}
                    bg="gray.700" 
                    color="white"
                    border="1px solid #4a5568"
                    placeholder="Select a vector store for file search..."
                  >
                    {fetchingVectorStores ? (
                      <option disabled>Loading vector stores...</option>
                    ) : vectorStores && vectorStores.length > 0 ? (
                      vectorStores.map((store) => (
                        <option key={store.id} value={store.id}>
                          {store.name} ({store.fileCount || 0} files)
                        </option>
                      ))
                    ) : (
                      <option disabled>No vector stores available</option>
                    )}
                  </Select>
                  {vectorStoresError && (
                    <Text color="red.400" fontSize="sm" mt={2}>
                      Error loading vector stores: {vectorStoresError.message}
                    </Text>
                  )}
                  {!formData.vectorStoreId && (
                    <Text color="yellow.400" fontSize="xs" mt={2}>
                      ⚠️ File Search Tool requires a vector store to function properly
                    </Text>
                  )}
                </FormControl>
              </VStack>
            </Box>
          )}

          {/* Tool validation info */}
          {formData.enabledTools.length > 0 && (
            <Box mt={4} p={3} bg="blue.900" borderRadius="md" border="1px solid blue.600">
              <Text color="blue.200" fontSize="sm" fontWeight="bold" mb={2}>✅ Enabled Tools:</Text>
              <HStack spacing={2} flexWrap="wrap">
                {formData.enabledTools.map((tool) => (
                  <Badge key={tool} colorScheme="blue" variant="solid">
                    {tool}
                  </Badge>
                ))}
              </HStack>
            </Box>
          )}
        </Box>

        <Divider borderColor="gray.600" />

        {/* Instructions Section */}
        <Box>
          <Heading size="md" color="white" mb={4}>Agent Instructions</Heading>
          <FormControl>
            <Textarea 
              value={formData.instructions}
              onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
              placeholder="Enter detailed instructions for this agent..."
              rows={8}
              bg="gray.700" 
              color="white"
              border="1px solid #4a5568"
            />
            <Text color="gray.400" fontSize="xs" mt={1}>
              Define the agent's personality, behavior, and response guidelines.
            </Text>
          </FormControl>
        </Box>

        <Divider borderColor="gray.600" />

        {/* Action Buttons */}
        <HStack spacing={4} justify="space-between">
          <HStack spacing={2}>
            <Button colorScheme="blue" onClick={handleSave} isLoading={updating}>
              Save Configuration
            </Button>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </HStack>
          
          <Button 
            colorScheme="purple" 
            onClick={handleSyncToVM}
            isLoading={syncing}
            leftIcon={'🔄'}
            size="md"
          >
            Sync to VM
          </Button>
        </HStack>
      </VStack>
    );
  };

  return (
    <Box bg="gray.900" minH="100vh">
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box textAlign="center">
            <Heading size="xl" color="white" mb={2}>
              🤖 Agent Control Panel
            </Heading>
            <Text color="gray.400">
              Manage and monitor your AI agents
            </Text>
          </Box>

          {/* Setup Alert */}
          {(!configurations || configurations.length === 0) && !fetchingConfigs && (
            <Alert status="warning" bg="orange.800" color="white" borderRadius="md">
              <AlertIcon />
              <VStack align="start" spacing={2}>
                <AlertTitle>Agent Configurations Not Found!</AlertTitle>
                <AlertDescription>
                  Initialize agent configurations to get started.
                </AlertDescription>
                <Button 
                  size="sm" 
                  colorScheme="orange" 
                  onClick={handleSetupAgents}
                  isLoading={settingUp}
                >
                  Setup Agents
                </Button>
              </VStack>
            </Alert>
          )}

          {/* Error Alerts */}
          {configsError && (
            <Alert status="error" bg="red.800" color="white" borderRadius="md">
              <AlertIcon />
              <AlertTitle>Configuration Error!</AlertTitle>
              <AlertDescription>{configsError.message}</AlertDescription>
            </Alert>
          )}

          {/* Main Content */}
          <Tabs variant="enclosed" onChange={(index) => setActiveTab(index)} bg="gray.800" borderRadius="md">
            <TabList borderColor="gray.600">
              <Tab color="white" _selected={{ color: 'blue.400', borderColor: 'blue.400' }}>
                Overview
              </Tab>
              <Tab color="white" _selected={{ color: 'blue.400', borderColor: 'blue.400' }}>
                Configuration
              </Tab>
              <Tab color="white" _selected={{ color: 'blue.400', borderColor: 'blue.400' }}>
                Testing
              </Tab>
              <Tab color="white" _selected={{ color: 'blue.400', borderColor: 'blue.400' }}>
                Debug
              </Tab>
            </TabList>

            <TabPanels>
              {/* Overview Tab */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  <HStack justify="space-between" align="center">
                    <Heading size="lg" color="white">Agent Overview</Heading>
                    <Button 
                      colorScheme="blue" 
                      onClick={handleSetupAgents}
                      isLoading={settingUp}
                      size="sm"
                    >
                      Refresh Setup
                    </Button>
                  </HStack>

                  {fetchingConfigs ? (
                    <Box textAlign="center" py={8}>
                      <Spinner size="lg" color="blue.400" />
                      <Text color="gray.400" mt={4}>Loading configurations...</Text>
                    </Box>
                  ) : (
                    <Grid templateColumns="repeat(auto-fit, minmax(400px, 1fr))" gap={6}>
                      {configurations?.map((config) => (
                        <GridItem key={config.id}>
                          <AgentCard config={config} />
                        </GridItem>
                      ))}
                    </Grid>
                  )}
                </VStack>
              </TabPanel>

              {/* Configuration Tab */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  <Heading size="lg" color="white">Agent Configuration</Heading>
                  
                  {editingAgent ? (
                    <Box bg="gray.800" p={6} borderRadius="md" border="1px solid #4a5568">
                      <Heading size="md" color="white" mb={4}>
                        Editing: {editingAgent.agentType?.toUpperCase()}
                      </Heading>
                      <ConfigurationEditor 
                        config={editingAgent}
                        onSave={handleUpdateConfig}
                        onCancel={() => setEditingAgent(null)}
                      />
                    </Box>
                  ) : (
                    <VStack spacing={4}>
                      <Text color="gray.400">Select an agent to configure</Text>
                      <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4} w="full">
                        {configurations?.map((config) => {
                          const agent = getAgentDisplayInfo(config);
                          return (
                            <Card 
                              key={config.id} 
                              bg="gray.800" 
                              border="1px solid #4a5568"
                              cursor="pointer"
                              onClick={() => setEditingAgent(config)}
                              _hover={{ bg: "gray.700" }}
                            >
                              <CardBody textAlign="center">
                                <Text fontSize="2xl" mb={2}>{agent.icon}</Text>
                                <Text color="white" fontWeight="bold">{config.agentType?.toUpperCase()}</Text>
                                <Badge colorScheme={config.isActive ? 'green' : 'red'} mt={2}>
                                  {config.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              </CardBody>
                            </Card>
                          );
                        })}
                      </Grid>
                    </VStack>
                  )}
                </VStack>
              </TabPanel>

              {/* Testing Tab */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  <Heading size="lg" color="white">Agent Testing</Heading>
                  
                  <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={6}>
                    {configurations?.map((config) => {
                      const agent = getAgentDisplayInfo(config);
                      return (
                        <Card key={config.id} bg="gray.800" border="1px solid #4a5568">
                          <CardBody>
                            <VStack spacing={4}>
                              <HStack>
                                <Text fontSize="xl">{agent.icon}</Text>
                                <Text color="white" fontWeight="bold">{agent.name}</Text>
                                <Badge colorScheme={config?.isActive ? 'green' : 'red'}>
                                  {config?.isActive ? 'Ready' : 'Offline'}
                                </Badge>
                              </HStack>
                              
                              <HStack spacing={2} w="full">
                                <Button 
                                  colorScheme="blue" 
                                  size="sm" 
                                  onClick={() => openTestModal(config)}
                                  isDisabled={!config?.isActive}
                                  flex={1}
                                >
                                  Quick Test
                                </Button>
                                <Button 
                                  colorScheme="green" 
                                  size="sm" 
                                  onClick={() => openChatInterface(config)}
                                  isDisabled={!config?.isActive}
                                  flex={1}
                                >
                                  Chat Test
                                </Button>
                              </HStack>

                              {testResults[agent.id] && (
                                <Box w="full" p={3} bg="gray.700" borderRadius="md">
                                  <Text fontSize="xs" color="gray.400">Last Test Result:</Text>
                                  <Badge colorScheme={testResults[agent.id].success ? 'green' : 'red'}>
                                    {testResults[agent.id].success ? 'Success' : 'Failed'}
                                  </Badge>
                                  {testResults[agent.id].responseTime && (
                                    <Text fontSize="xs" color="gray.300">
                                      {testResults[agent.id].responseTime}ms
                                    </Text>
                                  )}
                                </Box>
                              )}
                            </VStack>
                          </CardBody>
                        </Card>
                      );
                    })}
                  </Grid>
                </VStack>
              </TabPanel>

              {/* Debug Tab */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  <HStack justify="space-between">
                    <Heading size="lg" color="white">Debug Information</Heading>
                    <Switch 
                      isChecked={showDebug}
                      onChange={(e) => setShowDebug(e.target.checked)}
                      colorScheme="blue"
                    />
                  </HStack>

                  {showDebug && (
                    <Box bg="gray.800" p={4} borderRadius="md" border="1px solid #4a5568">
                      <Code colorScheme="gray" p={4} borderRadius="md" w="full">
                        <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
                      </Code>
                    </Box>
                  )}

                  <Grid templateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={4}>
                    <Card bg="gray.800" border="1px solid #4a5568">
                      <CardBody>
                        <Text color="white" fontWeight="bold" mb={2}>System Status</Text>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" color="gray.300">
                            Configurations: {configurations?.length || 0}
                          </Text>
                          <Text fontSize="sm" color="gray.300">
                            Active Agents: {configurations?.filter(c => c.isActive).length || 0}
                          </Text>
                          <Text fontSize="sm" color="gray.300">
                            Last Refresh: {new Date().toLocaleTimeString()}
                          </Text>
                        </VStack>
                      </CardBody>
                    </Card>

                    <Card bg="gray.800" border="1px solid #4a5568">
                      <CardBody>
                        <Text color="white" fontWeight="bold" mb={2}>Performance</Text>
                        <VStack align="start" spacing={1}>
                          {Object.entries(testResults).map(([agentId, result]) => (
                            <Text key={agentId} fontSize="sm" color="gray.300">
                              {agentId.toUpperCase()}: {result.responseTime || 0}ms
                            </Text>
                          ))}
                        </VStack>
                      </CardBody>
                    </Card>
                  </Grid>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>

          {/* Test Modal */}
          <Modal isOpen={isTestModalOpen} onClose={closeTestModal} size="lg">
            <ModalOverlay />
            <ModalContent bg="gray.800" border="1px solid #4a5568">
              <ModalHeader color="white">
                Test {testingAgent?.name} Agent
              </ModalHeader>
              <ModalCloseButton color="white" />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel color="white">Test Input</FormLabel>
                    <Textarea
                      value={testInput}
                      onChange={(e) => setTestInput(e.target.value)}
                      placeholder="Enter your test message..."
                      rows={4}
                      bg="gray.700"
                      color="white"
                      border="1px solid #4a5568"
                    />
                  </FormControl>
                  
                  {testing && (
                    <Box w="full">
                      <Text color="blue.400" mb={2}>Testing agent...</Text>
                      <Progress size="sm" isIndeterminate colorScheme="blue" />
                    </Box>
                  )}
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button 
                  colorScheme="blue" 
                  mr={3} 
                  onClick={() => handleTestAgent(testingAgent?.id, testInput)}
                  isLoading={testing}
                  isDisabled={!testInput.trim()}
                >
                  Run Test
                </Button>
                <Button variant="outline" onClick={closeTestModal}>
                  Cancel
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* Chat Modal */}
          <Modal isOpen={isChatModalOpen} onClose={closeChatModal} size="xl">
            <ModalOverlay />
            <ModalContent bg="gray.800" border="1px solid #4a5568" maxH="80vh">
              <ModalHeader color="white">
                Chat with {testingAgent?.name} Agent
              </ModalHeader>
              <ModalCloseButton color="white" />
              <ModalBody>
                <VStack spacing={4} h="400px">
                  <Box 
                    flex={1} 
                    w="full" 
                    overflowY="auto" 
                    bg="gray.700" 
                    p={4} 
                    borderRadius="md"
                    border="1px solid #4a5568"
                  >
                    {chatHistory.length === 0 ? (
                      <Text color="gray.400" textAlign="center">
                        Start a conversation with {testingAgent?.name}...
                      </Text>
                    ) : (
                      <VStack spacing={3} align="stretch">
                        {chatHistory.map((message, index) => (
                          <Box
                            key={index}
                            p={3}
                            bg={
                              message.type === 'user' ? 'blue.600' :
                              message.type === 'error' ? 'red.600' : 'green.600'
                            }
                            borderRadius="md"
                            alignSelf={message.type === 'user' ? 'flex-end' : 'flex-start'}
                            maxW="80%"
                          >
                            <Text color="white" fontSize="sm">
                              {message.content}
                            </Text>
                            <Text color="gray.300" fontSize="xs" mt={1}>
                              {new Date(message.timestamp).toLocaleTimeString()}
                              {message.responseTime && ` (${message.responseTime}ms)`}
                            </Text>
                          </Box>
                        ))}
                      </VStack>
                    )}
                  </Box>
                  
                  <HStack w="full">
                    <Input
                      value={currentMessage}
                      onChange={(e) => setCurrentMessage(e.target.value)}
                      placeholder="Type your message..."
                      onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                      bg="gray.700"
                      color="white"
                      border="1px solid #4a5568"
                    />
                    <Button 
                      colorScheme="blue" 
                      onClick={sendChatMessage}
                      isLoading={testing}
                      isDisabled={!currentMessage.trim()}
                    >
                      Send
                    </Button>
                  </HStack>
                </VStack>
              </ModalBody>
            </ModalContent>
          </Modal>
        </VStack>
      </Container>
    </Box>
  );
};

export default AgentControlPanel;